import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import PermissionsGate from "../../../utils/permissionGate";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import StandardModal from "../../common/Modal/StandardModal";
import { TABLE_DATE_FORMAT } from "../../../utils/Constants";
import Button from "../../library/Button";
import { AddOhsPerson, RemoveOhsPerson } from "../../../services/FirstAidBoxService";
import {
    invalidateFirstAidBoxQueries,
    useOhsPeople,
    useOhsPeopleCandidates
} from "../../../query/hooks/QueryHooksFirstAidBox";
import { PEOPLE_ROLES } from "./ohsConstants";
import { getOhsErrorMessage } from "./ohsUtils";
import { MutedText } from "./Ohs.styles";
import OhsUserCell from "./OhsUserCell";
import { RenderBrands, RenderUserLocation, RenderUserRole } from "../../../utils/CommonGroupingItem";

const MANAGE_PEOPLE_SCOPES = ["caohsmanageedit"];
const ROLE_LABELS = {
    [PEOPLE_ROLES.firstAider]: { singular: "First Aider", plural: "First Aiders" },
    [PEOPLE_ROLES.ohsRepresentative]: { singular: "OHS Representative", plural: "OHS Representatives" },
};

/**
 * First Aiders or Managers list - who can see the Boxes tab / the manager tabs. Adding and
 * removing people needs the OHS Management permission (caohsmanageedit).
 * @param {Object} props
 * @param {string} props.role first_aider | ohs_representative
 */
function People({ role }) {
    const roleLabel = ROLE_LABELS[role];
    const isFirstAiderList = role === PEOPLE_ROLES.firstAider;
    const { data: people, isLoading } = useOhsPeople(role);
    const { data: peopleCandidates, isLoading: isCandidatesLoading } = useOhsPeopleCandidates(role);
    const [search, setSearch] = useState("");
    const [candidateSearch, setCandidateSearch] = useState("");
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const filteredPeople = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        if (!searchText) {
            return people;
        }
        return people.filter((person) => {
            return (person.name || "").toLowerCase().includes(searchText)
                || (person.Email || "").toLowerCase().includes(searchText);
        });
    }, [people, search]);

    const filteredCandidates = useMemo(() => {
        const searchText = candidateSearch.trim().toLowerCase();
        if (!searchText) {
            return peopleCandidates;
        }
        return peopleCandidates.filter((candidate) => {
            return (candidate.label || "").toLowerCase().includes(searchText)
                || (candidate.Email || "").toLowerCase().includes(searchText);
        });
    }, [peopleCandidates, candidateSearch]);

    const handleAdd = async (candidate) => {
        try {
            await AddOhsPerson(role, candidate.value);
            await invalidateFirstAidBoxQueries();
        } catch (error) {
            console.log("People.js->handleAdd->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, `${candidate.label} could not be added.`), "error");
        }
    };

    const handleDelete = async (person) => {
        try {
            const confirmation = await Swal.fire({
                title: "Are you sure?",
                text: isFirstAiderList
                    ? "They will lose access to the Boxes tab."
                    : "They will lose access to the OHS Representative tabs.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "var(--button-color)",
                cancelButtonColor: "var(--states-alert)",
                confirmButtonText: "Yes, delete it!",
            });
            if (!confirmation.isConfirmed) {
                return;
            }

            await RemoveOhsPerson(role, person.user_id);
            await invalidateFirstAidBoxQueries();
        } catch (error) {
            console.log("People.js->handleDelete->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, `${person.name} could not be deleted.`), "error");
        }
    };

    // The same user columns as User Management's list, shared by this list and the Add popup.
    const userColumns = [
        {
            name: "User",
            selector: "Firstname",
            sortable: true,
            grow: 2,
            minWidth: "200px",
            cell: (row) => <OhsUserCell user={row} />,
        },
        {
            name: "Email",
            selector: "Email",
            sortable: true,
            grow: 2,
            minWidth: "220px",
            cell: (row) => (row.Email ? <span title={row.Email}>{row.Email}</span> : "-"),
        },
        {
            name: "Role",
            selector: "role_name",
            sortable: true,
            minWidth: "140px",
            cell: (row) => (row.role_name ? RenderUserRole(row.role_name).html : "-"),
        },
        {
            name: "Location",
            selector: "Location",
            sortable: true,
            minWidth: "130px",
            cell: (row) => (row.Location ? RenderUserLocation(row.Location).html : "-"),
        },
        {
            name: "Company",
            selector: "Company",
            sortable: true,
            minWidth: "140px",
            cell: (row) => (row.Company ? (
                <span className="feature-name">
                    <span className="textLimit100">{RenderBrands(row.Company, row.topbar_color).html}</span>
                </span>
            ) : "-"),
        },
    ];

    const peopleColumns = [
        ...userColumns,
        isFirstAiderList ? {
            name: "Assigned Boxes",
            grow: 2,
            minWidth: "180px",
            cell: (row) => (row.assigned_box_codes?.length ? row.assigned_box_codes.join(", ") : "-"),
        } : {
            name: "Assigned Buildings",
            grow: 2,
            minWidth: "200px",
            cell: (row) => (row.assigned_building_names?.length ? row.assigned_building_names.join(", ") : "-"),
        },
        {
            name: "Added",
            selector: "created_at",
            sortable: true,
            minWidth: "160px",
            cell: (row) => (
                <div>
                    <div>{moment(row.created_at).format(TABLE_DATE_FORMAT)}</div>
                    {row.added_by_name && <MutedText>by {row.added_by_name}</MutedText>}
                </div>
            ),
        },
        {
            name: "Action",
            width: "90px",
            right: true,
            cell: (row) => (
                <div className="assessment-08 btn-dropdown-grp">
                    <div className="as-buttons">
                        <PermissionsGate scopes={MANAGE_PEOPLE_SCOPES} errorProps={{ disabled: true }}>
                            <button
                                type="button"
                                className="btn btn-danger rounded-circle"
                                title="Delete"
                                onClick={() => handleDelete(row)}
                            >
                                <i className="fal fa-trash-alt"></i>
                            </button>
                        </PermissionsGate>
                    </div>
                </div>
            ),
        },
    ];

    const candidateColumns = [
        ...userColumns,
        {
            name: "Action",
            width: "90px",
            right: true,
            cell: (row) => (
                <div className="assessment-08 btn-dropdown-grp">
                    <div className="as-buttons">
                        <PermissionsGate scopes={MANAGE_PEOPLE_SCOPES} errorProps={{ disabled: true }}>
                            <button
                                type="button"
                                className="btn btn-primary rounded-circle"
                                title={`Add as ${roleLabel.singular}`}
                                onClick={() => handleAdd(row)}
                            >
                                <i className="fal fa-plus"></i>
                            </button>
                        </PermissionsGate>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <>
            <DataTableComponentFrontPagination
                data={filteredPeople}
                columns={peopleColumns}
                loading={isLoading}
                keyField="user_id"
                search={search}
                setSearch={setSearch}
                isExport={false}
                defaultSort={{ defaultSortColumn: "Firstname", defaultSortAsc: true }}
                tableButton={[
                    <PermissionsGate scopes={MANAGE_PEOPLE_SCOPES} errorProps={{ disabled: true }} key="add-person">
                        <Button onClick={() => setIsAddModalOpen(true)} title={`Add ${roleLabel.singular}`}>
                            <i className="fal fa-plus"></i>
                            Add {roleLabel.singular}
                        </Button>
                    </PermissionsGate>,
                ]}
            />
            <StandardModal
                show={isAddModalOpen}
                onHide={() => {
                    setIsAddModalOpen(false);
                    setCandidateSearch("");
                }}
                title={`Add ${roleLabel.singular}`}
                icon={<i className="fal fa-plus" />}
                size="lg"
                cancelLabel="Close"
            >
                <DataTableComponentFrontPagination
                    isInsidePopUp
                    data={filteredCandidates}
                    columns={candidateColumns}
                    loading={isCandidatesLoading}
                    keyField="value"
                    search={candidateSearch}
                    setSearch={setCandidateSearch}
                    isExport={false}
                    defaultSort={{ defaultSortColumn: "Firstname", defaultSortAsc: true }}
                />
            </StandardModal>
        </>
    );
}

export default People;
